import React from 'react';
import { SelectFieldComponent } from '../common/SelectFieldComponent';
import { useUnidadesMedida } from '../../hooks/useParametric';
import { type UnidadMedida } from '../../types/catalog';

export interface UnidadMedidaSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  onSelect?: (value: string | number, label: string) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter unidades de medida */
  filter?: (unidadMedida: UnidadMedida) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * UnidadMedidaSelect Component
 *
 * A reusable dropdown component that displays unidades de medida from parametric data.
 * Features:
 * - Automatic data loading from parametric store
 * - Loading states and error handling
 * - Filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 */
export const UnidadMedidaSelect: React.FC<UnidadMedidaSelectProps> = ({
  value,
  onChange,
  onSelect,
  label = 'Unidad de Medida',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const { data: unidadesMedida, loading, fetch: fetchUnidadesMedida } = useUnidadesMedida();

  // Fetch unidades de medida on component mount
  React.useEffect(() => {
    fetchUnidadesMedida();
  }, [fetchUnidadesMedida]);

  // Filter unidades de medida based on props
  const filteredUnidadesMedida = React.useMemo(() => {
    let filtered = unidadesMedida;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [unidadesMedida, filter]);

  // Convert unidades de medida to select options
  const options = React.useMemo(() => {
    return filteredUnidadesMedida.map((unidadMedida) => ({
      value: unidadMedida.idUnidadMedida,
      label: unidadMedida.descripcion,
    }));
  }, [filteredUnidadesMedida]);

  // Handle error state
  const displayError = error;

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      onSelect={onSelect}
      options={options}
      error={displayError}
      disabled={disabled || loading}
      placeholder={placeholder}
      loading={loading}
      sx={sx}
    />
  );
};

export default UnidadMedidaSelect;